import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  login,
  register,
  logout,
  forgetPassword,
  resetPassword,
  verifyIdentity,
  fetchAllCards,
  fetchAllBillingDetails
} from "../../api/authApi";
import {
  completeUserProfile,
  fetchUser,
  fetchUserPoints,
  fetchCurrentUserProfile,
  addNotification,
  getPendingNotification,
  markReadNotificationHandler
} from "../../api/userApi";

// Thunks for handling async actions
export const loginUser = createAsyncThunk(
  "auth/login",
  async (credentials, thunkAPI) => {
    try {
      const response = await login(credentials);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const registerUser = createAsyncThunk(
  "auth/register",
  async (userData, thunkAPI) => {
    try {
      const response = await register(userData);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const verifyIdentityUser = createAsyncThunk(
  "auth/verifyIdentity",
  async (sessionData, thunkAPI) => {
    try {
      const response = await verifyIdentity(sessionData);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  "auth/fetchUser",
  async (date, thunkAPI) => {
    try {
      const userData = localStorage.getItem("user");
      // if (userData) {
      const userId = JSON.parse(userData || {}).userId;
      const response = await fetchUser(userId, date);
      localStorage.setItem("user", JSON.stringify(response.user));
      return response;
      // }
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const addNotificationUser = createAsyncThunk(
  "auth/addNotificationUser",
  async (date, thunkAPI) => {
    try {
      const response = await addNotification(date);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const markReadNotification = createAsyncThunk(
  "auth/markReadNotification",
  async (_, thunkAPI) => {
    try {
      const response = await markReadNotificationHandler();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
export const getNotificationUser = createAsyncThunk(
  "auth/getNotificationUser",
  async (_, thunkAPI) => {
    try {
      const response = await getPendingNotification();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchCurrentUserProfileData = createAsyncThunk(
  "auth/fetchCurrentUserProfileData",
  async ({ date, diaryLevel }, thunkAPI) => {
    try {
      const userId = JSON.parse(localStorage.getItem("user") || {}).userId;
      const response = await fetchCurrentUserProfile(userId, date, diaryLevel);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getPointList = createAsyncThunk(
  "auth/getPointList",
  async (data, thunkAPI) => {
    try {
      const response = await fetchUserPoints(data);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const completeProfile = createAsyncThunk(
  "auth/completeProfile",
  async (userData, thunkAPI) => {
    try {
      const response = await completeUserProfile(
        userData.formData,
        userData.userId,
        userData.date
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, thunkAPI) => {
    try {
      const response = await logout();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const forgetPasswordUser = createAsyncThunk(
  "auth/forgetPassword",
  async (email, thunkAPI) => {
    try {
      const response = await forgetPassword(email);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const changePasswordUser = createAsyncThunk(
  "auth/resetPassword",
  async (userData, thunkAPI) => {
    try {
      const response = await resetPassword(userData);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchUserCards = createAsyncThunk(
  "auth/getCards",
  async (_, thunkAPI) => {
    try {
      const response = await fetchAllCards();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchUserBillingDetails = createAsyncThunk(
  "auth/fetchUserBillingDetails",
  async (_, thunkAPI) => {
    try {
      const response = await fetchAllBillingDetails();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

// Initial state
const initialState = {
  user:
    JSON.parse(
      ["undefined", "null", undefined, null].includes(
        localStorage.getItem("user")
      )
        ? null
        : localStorage.getItem("user")
    ) || null,
  isAuthenticated: !!localStorage.getItem("token"),
  loading: false,
  profileData: {},
  cards: [],
  billingDetails: {},
  pointLists: {},
  isPendingReadNotification: false,
  country: null,
  pointsLoading: false,
  error: null
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCountry: (state, action) => {
      state.country = action.payload;
    },
    markAsRead: (state, action) => {
      state.isPendingReadNotification = false;
    },
    updateUserUncompletedCount: (state) => {
      if (state.user.pendingTrainingDays)
        state.user.pendingTrainingDays = state.user.pendingTrainingDays - 1;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.loading = false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.loading = false;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      // Fetch user
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.loading = false;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
        state.isAuthenticated = false;
      })
      .addCase(fetchCurrentUserProfileData.fulfilled, (state, action) => {
        state.profileData = action.payload.user;
        state.loading = false;
      })
      .addCase(completeProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(completeProfile.fulfilled, (state, action) => {
        if (action.payload?.user?.token) {
          state.user = action.payload.user;
        }
        state.isAuthenticated = true;
        state.loading = false;
      })
      .addCase(completeProfile.rejected, (state, action) => {
        state.error = action.payload;
        state.isAuthenticated = false;
        state.loading = false;
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
      })
      .addCase(forgetPasswordUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
      })
      .addCase(changePasswordUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
      })
      .addCase(fetchUserCards.fulfilled, (state, action) => {
        state.cards = action.payload.cards;
      })
      .addCase(getPointList.pending, (state, action) => {
        state.pointsLoading = true;
      })
      .addCase(getPointList.fulfilled, (state, action) => {
        state.pointsLoading = false;
        state.pointLists = action.payload;
      })
      .addCase(getPointList.rejected, (state, action) => {
        state.pointsLoading = false;
      })
      .addCase(getNotificationUser.fulfilled, (state, action) => {
        state.isPendingReadNotification = action.payload.isPendingToRead;
      })
      .addCase(addNotificationUser.fulfilled, (state, action) => {
        state.isPendingReadNotification =
          action.payload.pendingNotificationsCount > 0 ? true : false;
      })
      .addCase(fetchUserBillingDetails.fulfilled, (state, action) => {
        state.billingDetails = action.payload;
      });
  }
});
export const { updateUserUncompletedCount, markAsRead, setCountry } =
  authSlice.actions;

export default authSlice.reducer;
